import {useCallback, useRef} from 'react'
import {ClockHandsIcon} from '@github-ui/custom-icons/ClockHandsIcon'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Button} from '@primer/react'

import {ForYouItem} from '../shared/ForYouItem'
import {useUnifiedBatchCount} from '../../../../stores/layout-selectors'
import {DockedPanel, useIsDockedPanelOpen} from '../../../../contexts/DockedPanelContext'
import {useOpenManageBatchPanel} from '../../../../hooks/use-open-manage-batch-panel'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

const ITEM_NAME = 'pending-batch'

export function shouldShowPendingBatch(unifiedBatchEnabled: boolean, batchCount: number) {
  return unifiedBatchEnabled && batchCount > 0
}

/**
 * Surfaces the viewer's unsubmitted batched changes so they can
 * recover that work and finish submitting it.
 */

export function PendingBatch() {
  const unifiedBatchEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const batchCount = useUnifiedBatchCount()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const isPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const openManageBatchPanel = useOpenManageBatchPanel('for_you_pending_batch')
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const handleOpen = useCallback(
    (currentTarget: HTMLButtonElement) => {
      sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
        item_name: ITEM_NAME,
        action_name: 'manage_batch',
      })
      anchorRef.current = currentTarget
      openManageBatchPanel({returnFocusTo: currentTarget})
    },
    [openManageBatchPanel, sendPullRequestAnalyticsEvent],
  )

  if (!shouldShowPendingBatch(unifiedBatchEnabled, batchCount)) return null

  const description = `${batchCount} unsubmitted item${batchCount === 1 ? '' : 's'} — only visible to you until submitted`

  return (
    <ForYouItem
      itemId={ITEM_NAME}
      visual={{kind: 'icon', icon: ClockHandsIcon, tone: 'attention', glyphSize: 24}}
      title="You have a batch in progress"
      subText={description}
      actions={[
        {
          id: 'manage-batch',
          content: (
            <Button
              ref={anchorRef}
              size="small"
              aria-expanded={isPanelOpen}
              onClick={({currentTarget}) => handleOpen(currentTarget)}
            >
              Manage batch
            </Button>
          ),
        },
      ]}
    />
  )
}
